import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, Instagram, Linkedin, Twitter, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { siteConfig } from '../../config/siteConfig';
import { getLocalizedPath } from '../../lib/utils';

/**
 * Footer complet avec liens, réseaux sociaux, newsletter et copyright
 */
export default function Footer() {
    const { t } = useTranslation();
    const { locale } = useParams<{ locale: string }>();
    const currentLocale = locale || 'fr';

    /* Liens rapides */
    const quickLinks = [
        { key: 'home', path: '' },
        { key: 'ecosystem', path: '/ecosysteme' },
        { key: 'services', path: '/services' },
        { key: 'blog', path: '/blog' },
        { key: 'contact', path: '/contact' },
    ];

    /* Liens réseaux sociaux */
    const socialLinks = [
        { icon: Facebook, href: siteConfig.social.facebook, label: 'Facebook' },
        { icon: Instagram, href: siteConfig.social.instagram, label: 'Instagram' },
        { icon: Linkedin, href: siteConfig.social.linkedin, label: 'LinkedIn' },
        { icon: Twitter, href: siteConfig.social.twitter, label: 'Twitter' },
        { icon: Youtube, href: siteConfig.social.youtube, label: 'YouTube' },
    ];

    return (
        <footer className="bg-navy-900 text-white relative overflow-hidden">
            {/* Décoration d'arrière-plan */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-0 right-0 w-96 h-96 bg-accent-500 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-navy-400 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
            </div>

            <div className="container-zig relative z-10">
                {/* Contenu principal */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 py-16 border-b border-navy-700/50">
                    {/* Branding */}
                    <div className="lg:col-span-1">
                        <Link to={getLocalizedPath(currentLocale, '')} className="flex items-center gap-2 mb-4 group">
                            <div className="w-10 h-10 rounded-xl bg-accent-gradient flex items-center justify-center shadow-glow">
                                <span className="text-white font-display font-bold text-lg"><img src="/zig.jpeg" alt="Zig Investment" className='w-10 h-10 rounded-xl' /></span>
                            </div>
                            <span className="font-display font-bold text-xl text-white">
                                ZIG Investment
                            </span>
                        </Link>
                        <p className="text-navy-300 text-sm leading-relaxed mb-6">
                            {t('footer.description')}
                        </p>
                        {/* Réseaux sociaux */}
                        <div className="flex gap-3">
                            {socialLinks.map(({ icon: Icon, href, label }) => (
                                <a
                                    key={label}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-10 h-10 rounded-xl bg-navy-800 flex items-center justify-center text-navy-300 hover:bg-accent-500 hover:text-white transition-all duration-300 hover:-translate-y-1"
                                    aria-label={label}
                                >
                                    <Icon className="w-4 h-4" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Liens rapides */}
                    <div>
                        <h3 className="font-display font-bold text-white mb-4">{t('footer.quickLinks')}</h3>
                        <ul className="space-y-3">
                            {quickLinks.map((link) => (
                                <li key={link.key}>
                                    <Link
                                        to={getLocalizedPath(currentLocale, link.path)}
                                        className="text-navy-300 hover:text-accent-400 text-sm transition-colors duration-200 hover:translate-x-1 inline-block"
                                    >
                                        {t(`nav.${link.key}`)}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h3 className="font-display font-bold text-white mb-4">{t('contact.info.title')}</h3>
                        <ul className="space-y-3">
                            <li className="flex items-start gap-3 text-sm text-navy-300">
                                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-accent-400" />
                                {siteConfig.contact.address.full}
                            </li>
                            <li className="flex items-center gap-3 text-sm text-navy-300">
                                <Phone className="w-4 h-4 shrink-0 text-accent-400" />
                                <a href={`tel:${siteConfig.contact.phones[0]}`} className="hover:text-accent-400 transition-colors">
                                    {siteConfig.contact.phones[0]}
                                </a>
                            </li>
                            <li className="flex items-center gap-3 text-sm text-navy-300">
                                <Mail className="w-4 h-4 shrink-0 text-accent-400" />
                                <a href={`mailto:${siteConfig.contact.emails[0]}`} className="hover:text-accent-400 transition-colors">
                                    {siteConfig.contact.emails[0]}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Légal & Newsletter */}
                    <div>
                        <h3 className="font-display font-bold text-white mb-4">{t('footer.legal')}</h3>
                        <ul className="space-y-3 mb-6">
                            <li>
                                <Link
                                    to={getLocalizedPath(currentLocale, '/privacy')}
                                    className="text-navy-300 hover:text-accent-400 text-sm transition-colors duration-200"
                                >
                                    {t('footer.privacy')}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to={getLocalizedPath(currentLocale, '/terms')}
                                    className="text-navy-300 hover:text-accent-400 text-sm transition-colors duration-200"
                                >
                                    {t('footer.terms')}
                                </Link>
                            </li>
                        </ul>

                        {/* Newsletter */}
                        <h3 className="font-display font-bold text-white mb-3">{t('footer.newsletter.title')}</h3>
                        <p className="text-navy-400 text-xs mb-3">{t('footer.newsletter.description')}</p>
                        <form className="flex gap-2" onSubmit={(e) => e.preventDefault()}>
                            <input
                                type="email"
                                placeholder={t('footer.newsletter.placeholder')}
                                className="flex-1 h-10 px-4 rounded-xl bg-navy-800 border border-navy-700 text-white text-sm placeholder:text-navy-500 focus:outline-none focus:ring-2 focus:ring-accent-500 transition-all"
                            />
                            <button
                                type="submit"
                                className="h-10 px-4 rounded-xl bg-accent-gradient text-white text-sm font-semibold hover:shadow-glow transition-all duration-300"
                            >
                                {t('footer.newsletter.subscribe')}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Copyright */}
                <div className="py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-navy-400 text-sm">
                        {t('footer.copyright', { year: new Date().getFullYear() })}
                    </p>
                    <p className="text-navy-500 text-sm">
                        {t('footer.madeWith')}
                        <a href={siteConfig.contruct.kolabtech} target="_blank" rel="noopener noreferrer" className="text-accent-400 hover:text-accent-500 transition-colors duration-200">
                            Kolabo Tech
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
}
